import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds";

const links = [
  { id: "weight",    label: "Weight Loss",      testid: "nav-link-weight",    href: "/category/weight-loss", to: true },
  { id: "womens",    label: "Women's Health",   testid: "nav-link-womens",    href: "/womens-shop", color: "pink", to: true },
  { id: "mens",      label: "Men's Health",     testid: "nav-link-mens",      href: "/mens-garage", color: "blue", to: true },
  { id: "wellness",  label: "Wellness",         testid: "nav-link-wellness",  href: "/category/wellness", to: true },
  { id: "zone",      label: "Medication Zone",  testid: NAV.linkShop,         href: "/#medication-zone" },
  { id: "learn",     label: "Resources",        testid: "nav-link-learn",     href: "/learn", to: true },
  { id: "results",   label: "Results",          testid: NAV.linkResults,      href: "/results", to: true },
  { id: "faq",       label: "FAQ",              testid: "nav-link-faq",       href: "/faq", to: true },
  { id: "providers", label: "Providers",        testid: "nav-link-providers", href: "/providers", to: true },
  { id: "about",     label: "About",            testid: "nav-link-about",     href: "/about", to: true },
  { id: "contact",   label: "Contact",          testid: "nav-link-contact",   href: "/contact", to: true },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const renderLink = (l, mobile = false) => {
    // Unified brand palette: all links use the same neutral color.
    // The hot-pink / blue category accents have been retired so every page
    // matches the hero's clean white-on-black-with-red-accent palette.
    const cls = mobile
      ? "font-display text-[22px] leading-none text-white"
      : "eyebrow transition-colors hover:text-white text-[var(--leen-ink-soft)]";
    if (l.to) {
      return (
        <Link key={l.id} to={l.href} data-testid={mobile ? `${l.testid}-mobile` : l.testid} onClick={mobile ? () => setOpen(false) : undefined} className={cls}>
          {l.label}
        </Link>
      );
    }
    // Anchor: if on home, hash-only; if on another page, prefix with /
    const finalHref = isHome && l.href.startsWith("/#") ? l.href.slice(1) : l.href;
    return (
      <a key={l.id} href={finalHref} data-testid={mobile ? `${l.testid}-mobile` : l.testid} onClick={mobile ? () => setOpen(false) : undefined} className={cls}>
        {l.label}
      </a>
    );
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[var(--leen-bg)]/90 backdrop-blur-xl border-b border-[var(--leen-line)]"
          : "bg-transparent"
      }`}
    >
      <div className="px-5 md:px-8 lg:px-10 h-16 md:h-20 flex items-center justify-between gap-6">
        {/* Logo */}
        <Link
          to="/"
          data-testid={NAV.logo}
          className="flex items-center shrink-0"
        >
          <img src="/assets/bestrx-logo-main.png" alt="BestRx" className="h-10 md:h-12 w-auto" />
        </Link>

        {/* Center nav */}
        <nav className="hidden xl:flex items-center gap-7">
          {links.map((l) => renderLink(l, false))}
        </nav>

        <a
          href={isHome ? "#start-assessment" : "/#start-assessment"}
          data-testid={NAV.ctaAssessment}
          className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-[12px] font-extrabold tracking-[0.16em] uppercase text-white"
          style={{ background: "#E11D2A" }}
        >
          Start Your Journey
          <span aria-hidden>→</span>
        </a>

        <button
          aria-label="Open menu"
          data-testid={NAV.menuToggle}
          onClick={() => setOpen((v) => !v)}
          className="xl:hidden p-2 -mr-2 text-white"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden bg-[var(--leen-bg)] border-t border-[var(--leen-line)]">
          <div className="px-6 py-8 flex flex-col gap-5">
            {links.map((l) => renderLink(l, true))}
            <a
              href={isHome ? "#start-assessment" : "/#start-assessment"}
              data-testid={`${NAV.ctaAssessment}-mobile`}
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center gap-2 mt-3 px-5 py-3 text-[12px] font-extrabold tracking-[0.16em] uppercase text-white"
              style={{ background: "#E11D2A" }}
            >
              Start Your Journey <span aria-hidden>→</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
